import {
  readSharedRefreshToken,
  writeSharedRefreshToken,
  clearSharedRefreshToken,
  markSharedSignOut,
  isSharedSignOut,
} from './sso';

const API_PREFIX = '/api/v1';

function getBaseUrl() {
  return process.env.VUE_APP_NUKLI_API_URL || '';
}

function getStoredTokens() {
  try {
    const raw = localStorage.getItem('nukli_tokens');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setStoredTokens(tokens) {
  if (tokens) {
    localStorage.setItem('nukli_tokens', JSON.stringify(tokens));
    // Punto único por el que pasan login, registro, OAuth y refresh: aquí se
    // publica el token para el otro origen (ver services/sso.js).
    writeSharedRefreshToken(tokens.refreshToken);
  } else {
    localStorage.removeItem('nukli_tokens');
    clearSharedRefreshToken();
    // El otro origen puede tener aún un access token válido: la marca hace que
    // cierre también en vez de resembrar la cookie con su copia del refresh.
    markSharedSignOut();
  }
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem('nukli_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setStoredUser(user) {
  if (user) {
    localStorage.setItem('nukli_user', JSON.stringify(user));
  } else {
    localStorage.removeItem('nukli_user');
  }
}

/** Canjea un refresh token concreto. Devuelve la respuesta o null si ya no vale. */
async function exchangeRefreshToken(refreshToken) {
  const base = getBaseUrl();
  if (!base || !refreshToken) return null;
  const res = await fetch(`${base}${API_PREFIX}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  }).catch(() => null);
  if (!res || !res.ok) return null;
  return res.json();
}

// Un solo refresh a la vez: el servidor rota el token, así que dos refrescos
// simultáneos (el bootstrap y un 401 de la misma pantalla, por ejemplo) harían
// que el segundo llegara con un token ya revocado.
let refreshInFlight = null;

function refreshTokens() {
  if (!refreshInFlight) {
    refreshInFlight = doRefreshTokens().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefreshTokens() {
  // Cerraron la sesión en el otro origen: no intentamos revivirla.
  if (isSharedSignOut()) {
    setStoredTokens(null);
    setStoredUser(null);
    return false;
  }

  const local = getStoredTokens();
  // La cookie compartida manda sobre el token local: si la app web refrescó
  // después que nosotros, el nuestro ya está revocado (el servidor rota).
  const shared = readSharedRefreshToken();
  const candidate = shared || local?.refreshToken;
  if (!candidate) return false;

  let data = await exchangeRefreshToken(candidate);
  if (!data) {
    // Carrera: el otro origen (u otra pestaña) pudo rotarlo mientras tanto.
    const latest = readSharedRefreshToken();
    if (latest && latest !== candidate) data = await exchangeRefreshToken(latest);
  }

  if (!data) {
    setStoredTokens(null);
    setStoredUser(null);
    return false;
  }

  setStoredTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  if (data.user) setStoredUser(data.user);
  return true;
}

/**
 * Rehidrata la sesión desde la cookie compartida con mail.nukliapp.com.
 * Llamar al arrancar (y antes de decidir un redirect a /login):
 *
 *   - hay cookie y no coincide con lo local -> la canjeamos: quien inició
 *     sesión en la app web entra aquí ya autenticado;
 *   - no hay cookie pero sí sesión local -> la sembramos, para que el viaje
 *     inverso (landing -> app web) también funcione.
 */
export async function bootstrapSharedSession() {
  if (isSharedSignOut()) {
    setStoredTokens(null);
    setStoredUser(null);
    return getSession();
  }

  const local = getStoredTokens();
  const shared = readSharedRefreshToken();

  if (!shared) {
    if (local?.refreshToken) writeSharedRefreshToken(local.refreshToken);
    return getSession();
  }
  if (!local?.accessToken || local.refreshToken !== shared) {
    await refreshTokens();
  }
  return getSession();
}

async function apiRequest(path, opts = {}) {
  const base = getBaseUrl();
  if (!base) throw new Error('NUKLI_API_URL not configured');

  const url = new URL(`${base}${API_PREFIX}${path}`);
  if (opts.query) {
    Object.entries(opts.query).forEach(([k, v]) => {
      if (v != null) url.searchParams.set(k, String(v));
    });
  }

  // El token se lee en CADA intento, no una vez fuera: si el 401 dispara un
  // refresh, el reintento tiene que ir con el token nuevo. Leerlo antes hacía
  // que el reintento repitiera el caducado y volviera a dar 401, de modo que
  // toda petición tras expirar el access token acababa mandando al login.
  const doFetch = () => {
    const accessToken = getStoredTokens()?.accessToken;
    return fetch(url.toString(), {
      method: opts.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  };

  let res = await doFetch();
  if (res.status === 401 && (await refreshTokens())) {
    res = await doFetch();
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = text;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || text;
    } catch {
      /* keep text */
    }
    const err = new Error(message || `API error ${res.status}`);
    err.status = res.status;
    throw err;
  }

  if (res.status === 204) return undefined;
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return undefined;
  return res.json();
}

export async function login(email, password) {
  const base = getBaseUrl();
  if (!base) throw new Error('NUKLI_API_URL not configured');

  const res = await fetch(`${base}${API_PREFIX}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    if (res.status === 401) throw new Error('invalid_credentials');
    const text = await res.text().catch(() => '');
    throw new Error(text || 'login_failed');
  }

  const data = await res.json();
  setStoredTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  setStoredUser(data.user);
  return data.user;
}

export async function register(email, password, displayName, referralCode) {
  const base = getBaseUrl();
  if (!base) throw new Error('NUKLI_API_URL not configured');

  const res = await fetch(`${base}${API_PREFIX}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `referralCode` (opcional): quien compartió el enlace /r/:code se lleva el
    // crédito de referido si esta cuenta acaba haciéndose premium.
    body: JSON.stringify({ email, password, displayName, referralCode }),
  });

  if (!res.ok) {
    if (res.status === 409) throw new Error('email_taken');
    const text = await res.text().catch(() => '');
    throw new Error(text || 'register_failed');
  }

  const data = await res.json();
  setStoredTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  setStoredUser(data.user);
  return data.user;
}

export async function logout() {
  const base = getBaseUrl();
  // Revocamos el token vivo de la cadena compartida: así el cierre de sesión
  // alcanza también a mail.nukliapp.com (su siguiente refresh dará 401).
  const refreshToken = readSharedRefreshToken() || getStoredTokens()?.refreshToken;
  if (base && refreshToken) {
    await fetch(`${base}${API_PREFIX}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => {});
  }
  setStoredTokens(null);
  setStoredUser(null);
}

/**
 * Solicita el email de recuperación de contraseña. Devuelve { ok, provider? }:
 * si la cuenta es social, `provider` es 'google'|'microsoft' para sugerir el
 * login social. Siempre resuelve ok aunque el email no exista (sin enumeración).
 */
export async function forgotPassword(email) {
  const base = getBaseUrl();
  if (!base) throw new Error('NUKLI_API_URL not configured');

  const res = await fetch(`${base}${API_PREFIX}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || 'forgot_failed');
  }
  return res.json();
}

/** Fija una nueva contraseña a partir del token recibido por email. */
export async function resetPassword(token, password) {
  const base = getBaseUrl();
  if (!base) throw new Error('NUKLI_API_URL not configured');

  const res = await fetch(`${base}${API_PREFIX}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text.includes('invalid_or_expired') ? 'invalid_or_expired' : 'reset_failed');
  }
  return res.json();
}

export function getSession() {
  return {
    user: getStoredUser(),
    tokens: getStoredTokens(),
    isAuthenticated: !!getStoredTokens()?.accessToken,
  };
}

/**
 * Usuario de la sesión actual, mirando primero al estado reactivo de la raíz.
 * `$root.nukliUser` se rellena en el `mounted` de main.ts, así que en el primer
 * pintado (o en un recargo directo) todavía es null aunque haya sesión: de ahí
 * el respaldo en localStorage. Único criterio de "hay sesión" para la UI —
 * el token suelto no basta, sin usuario no hay nada que pintar.
 */
export function resolveSessionUser($root) {
  if ($root && $root.nukliUser) return $root.nukliUser;
  const session = getSession();
  return session.isAuthenticated ? session.user : null;
}

export function restoreSessionFromCookies($cookies) {
  const accessToken = $cookies.get('nukli_access');
  const refreshToken = $cookies.get('nukli_refresh');
  const user = $cookies.get('nukli_user');
  if (accessToken && refreshToken) {
    setStoredTokens({ accessToken, refreshToken });
    if (user) setStoredUser(user);
  }
}

export function persistSessionToCookies($cookies, user) {
  const tokens = getStoredTokens();
  if (tokens) {
    $cookies.set('nukli_access', tokens.accessToken, '30d');
    $cookies.set('nukli_refresh', tokens.refreshToken, '30d');
  }
  if (user) {
    $cookies.set('nukli_user', user, '30d');
  }
}

export function clearSessionCookies($cookies) {
  $cookies.remove('nukli_access');
  $cookies.remove('nukli_refresh');
  $cookies.remove('nukli_user');
}

export async function getBillingStatus() {
  return apiRequest('/billing/status');
}

export async function billingCheckout(plan, interval) {
  return apiRequest('/billing/checkout', { method: 'POST', body: { plan, interval } });
}

export async function billingPortal() {
  return apiRequest('/billing/portal', { method: 'POST' });
}

// ----- Iniciar sesión con Google / Microsoft -----

/** Paso 1: pide al backend la URL de autorización del proveedor. */
export async function oauthStart(provider, redirect = '/account') {
  return apiRequest(`/auth/oauth/${provider}/start`, { query: { redirect } });
}

/**
 * Paso 2 (en /auth/callback): intercambia code+state por una sesión Nukli y la
 * persiste. Devuelve { user, mailbox, redirect, ... }.
 */
export async function oauthExchange(code, state) {
  const base = getBaseUrl();
  if (!base) throw new Error('NUKLI_API_URL not configured');
  const res = await fetch(`${base}${API_PREFIX}/auth/oauth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || 'oauth_exchange_failed');
  }
  const data = await res.json();
  setStoredTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  setStoredUser(data.user);
  return data;
}

/**
 * Paso 3 (opcional): URL para conectar el correo como buzón. Es un segundo
 * consentimiento con los scopes de correo (el login solo pide identidad).
 * Requiere sesión; el callback del backend redirige a /account.
 */
export async function mailboxConnectStart(provider, loginHint) {
  const providerId = provider === 'google' ? 'gmail' : provider;
  return apiRequest(`/oauth/${providerId}/start`, { query: { loginHint, web: 1 } });
}

// ----- Moderación del blog (solo admins de la allowlist BLOG_ADMIN_EMAILS) -----

/** Lista de artículos en moderación. status: 'pending' | 'approved' | 'rejected'. */
export async function blogModerationList(status = 'pending') {
  const res = await apiRequest('/blog/moderation', { query: status ? { status } : undefined });
  return (res && res.items) || [];
}

/** Aprueba un artículo: saldrá público en el siguiente `publish` del motor. */
export async function blogModerationApprove(slug) {
  return apiRequest(`/blog/moderation/${encodeURIComponent(slug)}/approve`, { method: 'POST' });
}

/** Rechaza un artículo (no se publica). */
export async function blogModerationReject(slug) {
  return apiRequest(`/blog/moderation/${encodeURIComponent(slug)}/reject`, { method: 'POST' });
}

// ----- Panel de administración (solo emails de la allowlist ADMIN_EMAILS) -----

/**
 * Métricas agregadas de negocio: altas, buzones, correos, planes y coste de IA
 * (totales, series diarias y medias por día/mes). `days` es la ventana de la
 * serie diaria y de los desgloses por modelo/feature.
 */
export async function adminStats(days = 30) {
  return apiRequest('/admin/stats', { query: { days } });
}

/** Listado paginado de usuarios registrados con plan, buzones y coste. */
export async function adminUsers({ limit = 50, offset = 0, q, plan, sort } = {}) {
  return apiRequest('/admin/users', { query: { limit, offset, q, plan, sort } });
}

/**
 * Concede un plan a mano a un usuario durante `months` meses. Con `plan: 'free'`
 * retira la concesión. No toca la suscripción de Stripe: se guarda como
 * entitlement interno y caduca sola.
 */
export async function adminSetUserPlan(userId, plan, months = 1) {
  return apiRequest(`/admin/users/${userId}/plan`, {
    method: 'POST',
    body: { plan, months },
  });
}

export { apiRequest, getBaseUrl };
